import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Fade,
  Stack,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { useAppSelector } from '../../hooks/useAuth';
import { useNotification } from '../../contexts/NotificationContext';

interface BulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  open,
  onClose,
  onUploadSuccess,
}) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const { showNotification } = useNotification();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file: File | undefined) => {
    if (!file) return;
    const allowedExtensions = ['.xlsx', '.xls'];
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (allowedExtensions.includes(fileExtension)) {
      setSelectedFile(file);
    } else {
      showNotification('Invalid file type. Please upload an XLSX or XLS file.', 'error');
      setSelectedFile(null);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    validateAndSetFile(file);
  }, []);

  const handleDownloadSample = () => {
    const link = document.createElement('a');
    link.href = '/samples/Vehicle_BulkInsert.xlsx';
    link.download = 'Vehicle_BulkInsert.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showNotification('Please select a file to upload.', 'error');
      return;
    }

    if (!user?.userid) {
      showNotification('User not authenticated. Please log in again.', 'error');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      await apiService.uploadVehicleBulk(formData, user.userid);
      setSelectedFile(null);
      showNotification('✅ File uploaded successfully! Vehicles are being processed.', 'success');
      onUploadSuccess();
      onClose();
    } catch (err: any) {
      showNotification(err.message || 'An error occurred during upload.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsDragging(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          color: 'white',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: 3,
        }}
      >
        Bulk Vehicle Upload
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ py: 3, px: 4 }}>
        <Typography variant="body1" sx={{ my: 2, color: 'text.secondary' }}>
          Download the sample Excel file, fill in your vehicle details, and upload it here.
        </Typography>

        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadSample}
          sx={{
            mb: 3,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 500,
            px: 2.5,
            py: 1,
            boxShadow: 2,
            backgroundColor: 'rgba(13, 44, 90, 1)',
            '&:hover': {
              backgroundColor: 'rgba(13, 44, 90, 0.85)',
            },
            color: 'white',
          }}
        >
          Sample File
        </Button>

        {/* Drag & Drop Upload Area */}
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload-input')?.click()}
          sx={{
            border: '2px dashed',
            borderColor: isDragging
              ? 'primary.main'
              : selectedFile
              ? 'success.main'
              : 'grey.400',
            borderRadius: 3,
            p: 2,
            textAlign: 'center',
            backgroundColor: isDragging
              ? 'primary.lighter'
              : selectedFile
              ? 'grey.50'
              : 'background.paper',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'grey.100',
            },
          }}
        >
          <input
            id="file-upload-input"
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleFileChange}
          />

          {selectedFile ? (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1.5}>
              <FileIcon color="success" sx={{ fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight={500}>
                {selectedFile.name}
              </Typography>
            </Stack>
          ) : (
            <>
              <CloudUploadIcon
                sx={{ fontSize: 60, color: isDragging ? 'primary.main' : 'text.disabled', mb: 1 }}
              />
              <Typography variant="h6" fontWeight={600}>
                {isDragging ? 'Drop your file here' : 'Click or drag file to upload'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports only .xlsx or .xls files
              </Typography>
            </>
          )}
        </Box>

        {/* Loader */}
        <Fade in={loading}>
          <Box sx={{ display: loading ? 'flex' : 'none', alignItems: 'center', mt: 3 }}>
            <CircularProgress size={22} sx={{ mr: 2 }} />
            <Typography>Uploading file...</Typography>
          </Box>
        </Fade>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions
        sx={{
          p: 3,
          backgroundColor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            px: 3,
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={!selectedFile || loading}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            px: 3,
            fontWeight: 600,
            boxShadow: 3,
          }}
        >
          {loading ? 'Uploading...' : 'Upload File'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadModal;
